import { Activity, ArrowUpRight, BookOpen, CalendarDays, ClipboardList, Crosshair, LayoutDashboard, Menu, ShieldCheck, TrendingUp, Users, X } from "lucide-react";
import { useEffect, useState } from "react";
import type { DashboardMeta, DashboardSnapshotV2 } from "../types";

export const routes = [
  { path: "/today", label: "Today", shortLabel: "Today", icon: CalendarDays },
  { path: "/my-team", label: "My Team", shortLabel: "Team", icon: Users },
  { path: "/matchup", label: "Matchup", shortLabel: "Matchup", icon: Crosshair },
  { path: "/league", label: "League", shortLabel: "League", icon: TrendingUp },
  { path: "/hitters", label: "Hitters", shortLabel: "Hitters", icon: LayoutDashboard },
  { path: "/pitchers", label: "Pitchers", shortLabel: "Pitchers", icon: Activity },
  { path: "/transactions", label: "Transactions", shortLabel: "Moves", icon: ClipboardList },
] as const;

function statusText(meta: DashboardMeta) {
  const asOf = meta.evidenceAsOf || meta.freshness[0]?.asOf;
  if (!asOf || !Number.isFinite(Date.parse(asOf))) return "Evidence time unknown";
  const age = Date.now() - Date.parse(asOf);
  return `${age > 86400000 || meta.dataQuality === "stale" ? "Stale evidence" : "Evidence"} ${new Date(asOf).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}`;
}

export function MethodDrawer({ method }: { method: DashboardSnapshotV2["method"] }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const drawer = document.querySelector<HTMLElement>(".method-drawer");
    drawer?.querySelector<HTMLElement>("button")?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
      if (event.key !== "Tab") return;
      const elements = Array.from(drawer?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], input, select, [tabindex="0"]') || []);
      const first = elements[0], last = elements.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); previous?.focus(); };
  }, [open]);
  const calibrationReady = Boolean(method.calibration && (method.calibration.outcomesCollected ?? 0) >= 4 && (method.calibration.scoredPredictions ?? 0) >= 48 && method.calibration.brierScore !== null && method.calibration.brierScore !== undefined);
  return (
    <>
      <button className="method-button" onClick={() => setOpen(true)}><BookOpen size={16} /> How this works</button>
      {open && <div className="drawer-scrim" onMouseDown={() => setOpen(false)}>
        <aside className="method-drawer" role="dialog" aria-modal="true" aria-labelledby="method-title" onMouseDown={(event) => event.stopPropagation()}>
          <button className="icon-button drawer-close" onClick={() => setOpen(false)} aria-label="Close method"><X /></button>
          <span className="eyebrow">Method & trust</span>
          <h2 id="method-title">How the desk reaches a call</h2>
          <p>{method.summary}</p>
          <h3>Hard guardrails</h3>
          <ul>{method.guardrails.map((item) => <li key={item}>{item}</li>)}</ul>
          <h3>Source notes</h3>
          <ul>{method.sourceNotes.map((item) => <li key={item}>{item}</li>)}</ul>
          <h3>Calibration</h3>
          {method.calibration ? <section className="calibration-summary" aria-label="Model calibration summary">
            <div><span className={`analysis-badge analysis-${method.calibration.status}`}>{method.calibration.status}</span><strong>{method.calibration.noLookaheadGate === false ? "No-lookahead gate failed" : calibrationReady ? "Accepted-only scoring" : "Collecting calibration outcomes"}</strong></div>
            <p>{method.calibration.summary}</p>
            <dl>
              <div><dt>Predictions</dt><dd>{method.calibration.predictionsCollected ?? method.calibration.sampleSize ?? "—"}</dd></div>
              <div><dt>Outcomes</dt><dd>{method.calibration.outcomesCollected ?? "—"}</dd></div>
              <div><dt>Scored</dt><dd>{method.calibration.scoredPredictions ?? "—"}</dd></div>
              <div><dt>Brier score</dt><dd>{calibrationReady ? method.calibration.brierScore?.toFixed(3) : "Collecting"}</dd></div>
            </dl>
            <small>{calibrationReady ? "Gate passed: at least 4 completed matchups and 48 scored category outcomes. Lower Brier is better." : `Calibration gate needs 4 completed matchups and 48 scored category outcomes; current evidence is ${method.calibration.outcomesCollected ?? 0} and ${method.calibration.scoredPredictions ?? 0}.`}</small>
          </section> : <p>Calibration is collecting predictions and outcomes; no accuracy claim is shown yet.</p>}
        </aside>
      </div>}
    </>
  );
}

export function Shell({ meta, method, currentPath, navigate, children, isFixture }: { meta: DashboardMeta; method: DashboardSnapshotV2["method"]; currentPath: string; navigate: (path: string) => void; children: React.ReactNode; isFixture: boolean }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const go = (path: string) => { navigate(path); setMobileOpen(false); };
  return (
    <div className="app-shell">
      <header className="topbar">
        <button className="icon-button mobile-menu" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle navigation"><Menu /></button>
        <button className="brand" onClick={() => go("/today")} aria-label="Decision Desk home">
          <span className="brand-mark">DD</span>
          <span><strong>Decision Desk</strong><small>Playoff Edition</small></span>
        </button>
        <div className="topbar-context">
          <span>{meta.weekLabel}</span>
          <span className={`quality-dot quality-${meta.dataQuality}`}><ShieldCheck size={15} />{isFixture ? "Demo data" : statusText(meta)}</span>
        </div>
      </header>
      <aside className={`sidebar ${mobileOpen ? "sidebar-open" : ""}`}>
        <div className="team-block"><span className="eyebrow">Your matchup</span><strong>{meta.teamName}</strong><small>vs {meta.opponentName}</small></div>
        <nav aria-label="Decision views">
          {routes.map(({ path, label, icon: Icon }) => (
            <button key={path} className={currentPath === path ? "active" : ""} onClick={() => go(path)} aria-current={currentPath === path ? "page" : undefined}>
              <Icon size={18} aria-hidden="true" />{label}
            </button>
          ))}
        </nav>
        <div className="sidebar-footer"><MethodDrawer method={method} /><span><ArrowUpRight size={13} /> Read-only intelligence</span></div>
      </aside>
      {mobileOpen && <button className="mobile-scrim" onClick={() => setMobileOpen(false)} aria-label="Close navigation" />}
      <main id="main-content">{children}</main>
      <nav className="bottom-nav" aria-label="Mobile decision views">
        {routes.map(({ path, shortLabel, icon: Icon }) => (
          <button key={path} className={currentPath === path ? "active" : ""} onClick={() => go(path)} aria-current={currentPath === path ? "page" : undefined}>
            <Icon size={18} aria-hidden="true" /><span>{shortLabel}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

export function PageHeading({ eyebrow, title, summary, aside }: { eyebrow: string; title: string; summary: string; aside?: React.ReactNode }) {
  return <header className="page-heading"><div><span className="eyebrow">{eyebrow}</span><h1>{title}</h1><p>{summary}</p></div>{aside}</header>;
}

export function LoadingScreen() {
  return <main className="state-screen"><span className="loading-ball" aria-hidden="true" /><h1>Building your game plan…</h1><p>Checking the latest accepted league snapshot.</p></main>;
}

export function ErrorScreen({ error, retry }: { error: string; retry: () => void }) {
  return <main className="state-screen"><span className="state-icon">!</span><h1>The desk could not load safely.</h1><p>{error}</p><button className="primary-button" onClick={retry}>Try again</button></main>;
}
